import { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionCard } from "@/components/shared/PageChrome";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/* D5: report a repair against a current or upcoming placement. Eligibility comes from the server and is re-checked on submit. */
export function RepairReportForm({ preselectStayId, onSubmitted }) {
  const [eligible, setEligible] = useState(null);
  const [stayId, setStayId] = useState(preselectStayId || "");
  const [description, setDescription] = useState("");
  const [where, setWhere] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/client/repairs/eligible-placements").then((r) => setEligible(r.data.items)).catch(() => setEligible([])); }, []);
  useEffect(() => { if (eligible?.length === 1 && !stayId) setStayId(eligible[0].stay_id); }, [eligible, stayId]);

  const submit = async () => {
    const stay = eligible?.find((s) => s.stay_id === stayId);
    if (!stay) return toast.error("Choose the placement the repair relates to.");
    if (description.trim().length < 10) return toast.error("Describe the problem in at least 10 characters.");
    setBusy(true);
    try {
      const r = await api.post("/client/repairs", { booking_id: stay.booking_id, stay_id: stay.stay_id, description: description.trim(), location_in_property: where.trim() || null });
      toast.success(`Repair reported on ${r.data.booking_reference} - Jigsaw will review it`);
      setDescription(""); setWhere(""); onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit this repair report."); } finally { setBusy(false); }
  };

  return (
    <SectionCard testId="repair-report-form" title="Report a repair" subtitle="For a current or upcoming placement. Jigsaw reviews the report in the Support Inbox; a submitted report is not yet an accepted or scheduled repair.">
      {eligible === null && <p className="text-sm text-jigsaw-muted">Checking your placements…</p>}
      {eligible?.length === 0 && <p data-testid="repair-report-none-eligible" className="text-sm text-jigsaw-muted">You have no current or upcoming placements, so there is nothing to report a repair against. Completed, cancelled and historical placements cannot receive new repair reports.</p>}
      {eligible?.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="repair-stay">Placement</Label>
            <select id="repair-stay" data-testid="repair-report-stay-select" value={stayId} onChange={(e) => setStayId(e.target.value)} className="h-10 w-full rounded-md border border-jigsaw-border bg-white px-3 text-sm">
              <option value="">Choose a placement…</option>
              {eligible.map((s) => <option key={s.stay_id} value={s.stay_id} label={`${s.booking_reference} · ${s.property_name} · ${s.check_in_date} → ${s.check_out_date} (${s.phase.toLowerCase()})`} />)}
            </select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="repair-where">Where in the property (optional)</Label><Input id="repair-where" data-testid="repair-report-where" value={where} onChange={(e) => setWhere(e.target.value)} placeholder="e.g. Room 4 bathroom" /></div>
          <div className="space-y-1.5"><Label htmlFor="repair-desc">What needs repairing?</Label><Textarea id="repair-desc" data-testid="repair-report-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the fault and any safety concern. Do not include sensitive personal details." /></div>
          <Button data-testid="repair-report-submit" onClick={submit} disabled={busy}><Wrench className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Sending…" : "Submit repair report"}</Button>
        </div>
      )}
    </SectionCard>
  );
}

const REPAIR_LABEL = { SUBMITTED: "Submitted - awaiting Jigsaw review", ACCEPTED: "Accepted by Jigsaw - repair in hand", RESOLVED: "Resolved" };
const REPAIR_TONE = { SUBMITTED: "bg-blue-50 text-blue-700 border-blue-200", ACCEPTED: "bg-jigsaw-warningBg text-jigsaw-warning border-jigsaw-warning/30", RESOLVED: "bg-jigsaw-successBg text-jigsaw-success border-jigsaw-success/30" };

export function RepairCard({ note, children }) {
  const r = note.repair || {};
  return (
    <div data-testid={`repair-report-${note.id}`} data-status={r.status} className="rounded-[10px] border border-jigsaw-border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium text-jigsaw-muted"><Wrench className="h-3.5 w-3.5" /> Repair report · {r.booking_reference} · {r.property_name}</p>
        <span data-testid={`repair-status-${note.id}`} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${REPAIR_TONE[r.status] || ""}`}>{REPAIR_LABEL[r.status] || r.status}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-jigsaw-navy">{note.body}</p>
      {r.staff_note && <p className="mt-2 text-xs text-jigsaw-muted">Jigsaw: {r.staff_note}</p>}
      <p className="mt-2 text-xs text-jigsaw-muted">Reported {new Date(note.created_at).toLocaleString("en-GB")}{r.status_updated_at && r.status !== "SUBMITTED" ? ` · status updated ${new Date(r.status_updated_at).toLocaleString("en-GB")}` : ""}</p>
      {children}
    </div>
  );
}
